import React, { useMemo, useCallback } from 'react';
import { Creatable } from '@iq/react-components';
import '../widgets/SelectWidget.scss';

const CreatableField = (props) => {
  const {
    disabled,
    readonly,
    required,
    formData: value,
    schema: {
      handleCreatable,
      validateCreatable,
      default: defaultValue,
      isMulti,
      isClearable,
      noOptionsMessage,
      placeholder: schemaPlaceholder,
      items: { oneOf } = { oneOf: [] },
      label,
    },
    onChange,
    ...otherProps
  } = props;
  const getLabel = (val) => oneOf.find(({ enum: [v] }) => v === val)?.title || val;

  const options = useMemo(
    () =>
      oneOf.map(({ enum: [val], title }) => ({
        value: val,
        label: title,
      })),
    [oneOf]
  );

  const existingOptionsSet = useMemo(
    () => new Set(options.map((option) => option.value.toLowerCase())),
    [options]
  );

  const theValue = useMemo(() => {
    if (isMulti) {
      return (value || []).map((v) => ({
        value: v,
        label: oneOf.find(({ enum: [val] }) => val === v).title,
      }));
    }
    if (value || value === 0) {
      return { label: getLabel(value), value };
    }
    if (defaultValue) {
      return { label: getLabel(defaultValue), defaultValue };
    }
    return undefined;
  }, [value, defaultValue, oneOf]);

  const setValues = useCallback(
    (selected) => {
      if (isMulti) {
        onChange((selected || []).map((s) => s.value));
      } else {
        onChange(selected && (selected.value || selected.value === 0) ? selected.value : null);
      }
    },
    [onChange, isMulti]
  );

  const getEmptyMsg = (text) => <div>{text}</div>;

  const overrideStyles = {
    container: (base) => ({ ...base, lineHeight: 'normal' }),
    singleValue: (base) => ({ ...base, lineHeight: 'normal' }),
    control: (base) => ({ ...base, lineHeight: 'normal', maxWidth: '35rem' }),
  };

  const customIsValidNewOption = useCallback(
    (inputValue) => {
      if (!validateCreatable || !validateCreatable(inputValue)) {
        return false;
      }
      return !existingOptionsSet.has(inputValue.toLowerCase());
    },
    [validateCreatable, existingOptionsSet]
  );

  return (
    <>
      <label className="control-label">{label}</label>
      <Creatable
        {...otherProps}
        className="schema-select-widget"
        isMulti={isMulti}
        isClearable={isClearable}
        closeMenuOnSelect={!isMulti}
        disabled={disabled}
        readOnly={readonly}
        required={required}
        placeholder={schemaPlaceholder}
        menuPlacement="auto"
        value={theValue}
        onChange={setValues}
        isValidNewOption={customIsValidNewOption}
        onCreateOption={handleCreatable}
        options={options}
        noOptionsMessage={() => getEmptyMsg(noOptionsMessage || 'No options')}
        styles={overrideStyles}
      />
    </>
  );
};

export default CreatableField;
